import React, { useState, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query'; // eslint-disable-line
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, DollarSign, Package, FileDown, Calendar, ArrowUpRight, ArrowDownRight, BarChart2 } from 'lucide-react';
import { format, isAfter, isBefore, isEqual, startOfDay } from 'date-fns';
import { id } from 'date-fns/locale';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { useClientPOs } from '@/hooks/useClientPOs';
import CustomDatePicker from '@/components/shared/CustomDatePicker';

const fmt = (n) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const fmtNum = (n) => new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const fmtPct = (n) => `${(n || 0).toFixed(1)}%`;

const parseInvoiceDate = (dStr) => {
  if (!dStr) return 0;
  let d = new Date(dStr);
  if (isNaN(d.getTime()) && typeof dStr === 'string') {
    const str = dStr.toLowerCase();
    const months = { 'jan':0,'feb':1,'mar':2,'apr':3,'mei':4,'may':4,'jun':5,'jul':6,'agu':7,'aug':7,'sep':8,'okt':9,'oct':9,'nov':10,'des':11,'dec':11 };
    const parts = str.split(' ');
    if (parts.length >= 3) {
       const day = parseInt(parts[0]);
       const monthStr = Object.keys(months).find(m => parts[1].includes(m));
       const month = monthStr ? months[monthStr] : 0;
       const year = parseInt(parts[2]);
       d = new Date(year, month, day);
    }
  }
  return isNaN(d.getTime()) ? 0 : d.getTime();
};

function isInRange(dateStr, start, end) {
  if (!dateStr) return false;
  try {
    const timestamp = parseInvoiceDate(dateStr);
    if (timestamp === 0) return false;
    const d = startOfDay(new Date(timestamp));
    const s = startOfDay(new Date(start));
    const e = startOfDay(new Date(end));
    return (isAfter(d, s) || isEqual(d, s)) && (isBefore(d, e) || isEqual(d, e));
  } catch { return false; }
}

const PRESETS = [
  { label: 'Hari ini', getValue: () => { const t = format(new Date(), 'yyyy-MM-dd'); return [t, t]; } },
  { label: '7 Hari', getValue: () => { const now = new Date(); return [format(new Date(now - 6 * 86400000), 'yyyy-MM-dd'), format(now, 'yyyy-MM-dd')]; } },
  { label: '30 Hari', getValue: () => { const now = new Date(); return [format(new Date(now - 29 * 86400000), 'yyyy-MM-dd'), format(now, 'yyyy-MM-dd')]; } },
  { label: 'Bulan Ini', getValue: () => { const now = new Date(); return [format(new Date(now.getFullYear(), now.getMonth(), 1), 'yyyy-MM-dd'), format(now, 'yyyy-MM-dd')]; } },
  { label: 'Semua', getValue: () => ['2000-01-01', '2099-12-31'] },
];

export default function MarginSample() {
  const printRef = useRef(null);
  
  const [activePreset, setActivePreset] = useState('Semua');
  const [dateStart, setDateStart] = useState('2000-01-01');
  const [dateEnd, setDateEnd] = useState('2099-12-31');
  const [isCustom, setIsCustom] = useState(false);

  const { clientPOs = [], isLoading: loadingPOs } = useClientPOs();

  const handlePreset = (preset) => {
    const [s, e] = preset.getValue();
    setDateStart(s);
    setDateEnd(e);
    setActivePreset(preset.label);
    setIsCustom(false);
  };

  const invoiceList = useMemo(() => {
    const list = clientPOs.filter(po => {
      return po.poMode === "sample" || po.isSample === true;
    }).map(po => {
      const prodJual = (po.products || po.items || []).reduce((s, p) => s + (p.total_harga_jual || p.subtotal || 0), 0);
      const legJual = (po.legalitas || []).reduce((s, l) => s + (l.harga_jual * (l.qty_variant || 1)), 0);
      const totalJual = prodJual + legJual;

      const prodHpp = (po.products || []).reduce((s, p) => {
        if (p.total_hpp_product !== undefined && Number(p.total_hpp_product) > 0) {
          return s + Number(p.total_hpp_product);
        }
        const qty = p.qty || 1;
        if (p.hpp_satuan !== undefined) {
          return s + (Number(p.hpp_satuan) * qty);
        }
        const modalPackaging = (p.botol_harga_beli || 0) + (p.tutup_harga_beli || 0) + (p.spray_harga_beli || 0) + (p.fullset_harga_beli || 0);
        const volume = p.botol_ukuran_client || p.botol_ukuran_label || p.ukuran_botol_ml || p.volume_ml || 0;
        const modalRacikan = (p.racikan || []).reduce((rs, r) => {
          const costPerUnit = r.hpp_bahan || r.harga_per_satuan || 0;
          const volPerPcs = volume * ((parseFloat(String(r.persentase).replace(',', '.')) || 0) / 100);
          return rs + volPerPcs * (costPerUnit / 1000);
        }, 0);
        const modalKomponen = (p.komponen_tambahan || []).reduce((ks, k) => ks + (k.harga || 0), 0);
        const jasaNominal = p.jasa_nominal || p.final_jasa_nominal || 0;
        const customHargaBeli = p.hargaBeli || p.harga_beli || 0;
        const hppPerPcs = modalPackaging + modalRacikan + modalKomponen + jasaNominal + customHargaBeli;
        return s + (hppPerPcs * qty);
      }, 0);
      const legHpp = (po.legalitas || []).reduce((s, l) => s + ((l.harga_beli || 0) * (l.qty_variant || 1)), 0);
      const totalHpp = prodHpp + legHpp;

      const totalBayar = (po.riwayatPembayaran || []).reduce((s, r) => s + (r.jumlah || 0), 0);

      let statusBayarMapped = 'Belum Lunas';
      if (po.statusBayar === 'LUNAS') statusBayarMapped = 'Sudah Lunas';
      else if (po.statusBayar === 'BELUM DIBAYAR' || po.statusBayar === 'BELUM BAYAR') statusBayarMapped = 'Belum Bayar';
      else if (po.statusBayar) statusBayarMapped = po.statusBayar;

      return {
        id: po.id,
        tanggal: (po.statusProduksi === 'PESANAN SELESAI' || po.statusProduksi === 'SELESAI') && (po.updated_at || po.rawRecord?.updated_at) ? (po.updated_at || po.rawRecord.updated_at).split('T')[0] : (po.date || po.created_at?.split('T')[0] || ''),
        jatuh_tempo: po.dueDate || po.jatuh_tempo || po.jatuhTempo || null,
        nama_brand: po.customerName || 'Client Kustom',
        product_name: po.varianLabel || po.items?.map(i => i.nama).join(', ') || '-',
        status_bayar: statusBayarMapped,
        total_hpp_semua: totalHpp,
        total_harga_jual_semua: totalJual,
        jumlah_dibayar: totalBayar,
        raw: po
      };
    }).filter(item => item.total_hpp_semua > 0);
    
    return list.sort((a, b) => {
      const dateA = parseInvoiceDate(a.tanggal);
      const dateB = parseInvoiceDate(b.tanggal);
      return dateB - dateA;
    });
  }, [clientPOs]);

  const filtered = useMemo(() => {
    return invoiceList.filter(r => isInRange(r.tanggal, dateStart, dateEnd));
  }, [invoiceList, dateStart, dateEnd]);

  const getRecordMetrics = (r) => {
    const hpp = r.total_hpp_semua || 0;
    const jual = r.total_harga_jual_semua || 0;
    const dibayar = r.jumlah_dibayar || 0;
    const status = String(r.status_bayar || 'Belum Bayar');
    
    if (status === 'Sudah Lunas' || status === 'LUNAS' || status.toUpperCase() === 'LUNAS') {
      return { hpp, jual, margin: jual - hpp };
    }
    
    const actualMargin = dibayar - hpp;
    return { hpp, jual: dibayar, margin: actualMargin };
  };

  const summary = useMemo(() => {
    let totalHPP = 0, totalJual = 0, totalMargin = 0;
    filtered.forEach(r => {
      const { hpp, jual, margin } = getRecordMetrics(r);
      totalHPP += hpp;
      totalJual += jual;
      totalMargin += margin;
    });
    
    const marginPct = totalJual > 0 ? (totalMargin / totalJual) * 100 : 0;
    return { totalHPP, totalJual, totalMargin, marginPct, count: filtered.length };
  }, [filtered]);

  const allBrandData = useMemo(() => {
    const map = {};
    filtered.forEach(r => {
      const { hpp, jual, margin } = getRecordMetrics(r);
      const key = r.nama_brand || 'Unknown';
      if (!map[key]) map[key] = { brand: key, hpp: 0, jual: 0, margin: 0 };
      map[key].hpp += hpp;
      map[key].jual += jual;
      map[key].margin += margin;
    });
    return Object.values(map).sort((a, b) => b.margin - a.margin);
  }, [filtered]);

  const chartDataBrand = useMemo(() => allBrandData.slice(0, 10), [allBrandData]);

  const handleExportPDF = () => {
    const printHTML = `
      <html>
        <head>
          <title>Laporan Margin Sample - Satala Dermatech</title>
          <style>
            * { box-sizing: border-box; margin: 0; padding: 0; }
            @page { size: A4 landscape; margin: 15mm; }
            body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 11px; color: #302621; background: white; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .header { text-align: center; margin-bottom: 24px; border-bottom: 2px solid #8C6208; padding-bottom: 16px; }
            .header h1 { font-size: 22px; font-weight: 900; color: #302621; }
            .header p { color: #8C6208; font-size: 11px; margin-top: 6px; font-weight: 600; }
            .summary-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin-bottom: 24px; }
            .summary-card { border: 1px solid #E6D5B8; border-radius: 12px; padding: 14px; background: #F9F5EC; }
            .summary-card .label { font-size: 9px; color: #7A695B; text-transform: uppercase; font-weight: bold; letter-spacing: 0.05em; margin-bottom: 6px; }
            .summary-card .value { font-size: 16px; font-weight: 900; color: #302621; }
            .summary-card .sub { font-size: 10px; margin-top: 3px; font-weight: 500; }
            .green { color: #16a34a; } .red { color: #dc2626; } .blue { color: #2563eb; } .brand-color { color: #8C6208; }
            table { width: 100%; border-collapse: collapse; margin-top: 16px; }
            th { background: #F9F5EC; padding: 10px; text-align: left; font-size: 9px; text-transform: uppercase; font-weight: bold; color: #7A695B; border-bottom: 1.5px solid #E6D5B8; }
            td { padding: 10px; font-size: 11px; border-bottom: 1px solid #E6D5B8/40; color: #302621; }
            tr:nth-child(even) td { background: #FCFAF5; }
            .text-right { text-align: right; }
            .text-center { text-align: center; }
            .badge-green { background: #dcfce7; color: #16a34a; padding: 2px 8px; border-radius: 9999px; font-size: 10px; font-weight: 600; }
            .badge-red { background: #fee2e2; color: #dc2626; padding: 2px 8px; border-radius: 9999px; font-size: 10px; font-weight: 600; }
            .section-title { font-size: 13px; font-weight: bold; color: #302621; margin-bottom: 10px; margin-top: 20px; border-left: 3px solid #8C6208; padding-left: 8px; }
            .footer { margin-top: 40px; text-align: center; font-size: 10px; color: #A39171; border-top: 1px solid #E6D5B8; padding-top: 16px; font-weight: 500; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Laporan Margin Sample</h1>
            <p>Periode: ${dateStart} s/d ${dateEnd} &nbsp;|&nbsp; Dicetak: ${format(new Date(), 'dd MMMM yyyy HH:mm', { locale: id })}</p>
          </div>

          <div class="summary-grid">
            <div class="summary-card">
              <div class="label">Total Transaksi</div>
              <div class="value brand-color">${summary.count}</div>
              <div class="sub" style="color:#7A695B">invoice</div>
            </div>
            <div class="summary-card">
              <div class="label">Total HPP (Modal)</div>
              <div class="value red">${fmt(summary.totalHPP)}</div>
              <div class="sub" style="color:#dc2626">pengeluaran</div>
            </div>
            <div class="summary-card">
              <div class="label">Total Penjualan</div>
              <div class="value blue">${fmt(summary.totalJual)}</div>
              <div class="sub" style="color:#2563eb">pemasukan aktual</div>
            </div>
            <div class="summary-card">
              <div class="label">Margin Keuntungan</div>
              <div class="value green">${fmt(summary.totalMargin)}</div>
              <div class="sub green">${fmtPct(summary.marginPct)} dari penjualan</div>
            </div>
          </div>

          <div class="section-title">Rincian Per Invoice</div>
          <table>
            <thead>
              <tr>
                <th>Tanggal</th>
                <th>Nama Brand</th>
                <th>Produk</th>
                <th class="text-right">HPP (Modal)</th>
                <th class="text-right">Total Pembayaran</th>
                <th class="text-right">Total Margin</th>
                <th class="text-center">% Margin</th>
                <th class="text-center">Status</th>
              </tr>
            </thead>
            <tbody>
              ${filtered.map(r => {
                const { hpp, jual, margin } = getRecordMetrics(r);
                const pct = jual > 0 ? (margin / jual) * 100 : 0;
                const status = String(r.status_bayar || 'Belum Bayar');
                const isLunas = status === 'Sudah Lunas' || status.toUpperCase().includes('LUNAS');
                const isPartial = (r.jumlah_dibayar > 0 && r.jumlah_dibayar < (r.total_harga_jual_semua || 0)) || status.toUpperCase().includes('DP') || status === 'Belum Lunas' || status.toUpperCase() === 'TEMPO';
                const percentPaid = r.total_harga_jual_semua > 0 ? Math.round(((r.jumlah_dibayar || 0) / r.total_harga_jual_semua) * 100) : 0;
                const belumBayar = status === 'Belum Bayar' || status === 'BELUM BAYAR' || status === 'BELUM DIBAYAR';
                
                let displayStatus = status;
                if (isLunas) displayStatus = 'Sudah Lunas';
                else if (isPartial) displayStatus = `${percentPaid}% Menuju Lunas`;
                else if (belumBayar) displayStatus = 'Belum Dibayar';

                const timestamp = parseInvoiceDate(r.tanggal);
                const tanggal = timestamp ? format(new Date(timestamp), 'dd MMM yyyy', { locale: id }) : (r.tanggal || '—');

                const statusClass = margin >= 0 ? 'badge-green' : 'badge-red';
                const statusLabel = `<span class="${statusClass}">${margin >= 0 ? 'Untung' : 'Rugi'}</span>`;
                
                return `<tr>
                  <td>${tanggal}</td>
                  <td>
                    <strong>${r.nama_brand ? r.nama_brand.split(' / ')[0].trim() : '—'}</strong><br/>
                    <small style="color:#7A695B">${r.id} (${displayStatus})</small>
                    ${r.jatuh_tempo && !isLunas ? `<br/><small style="color:#dc2626; font-weight:bold;">Tempo: ${r.jatuh_tempo}</small>` : ''}
                  </td>
                  <td>${r.product_name}</td>
                  <td class="text-right" style="color:#dc2626">${fmt(hpp)}</td>
                  <td class="text-right" style="color:#2563eb">${fmt(jual)}</td>
                  <td class="text-right" style="color:${margin >= 0 ? '#16a34a' : '#dc2626'}"><strong>${fmt(margin)}</strong></td>
                  <td class="text-center" style="color:${pct >= 0 ? '#16a34a' : '#dc2626'}">${fmtPct(pct)}</td>
                  <td class="text-center">${statusLabel}</td>
                </tr>`;
              }).join('')}
            </tbody>
            <tfoot>
              <tr style="background:#F9F5EC; font-weight:bold; border-top: 2px solid #E6D5B8;">
                <td colspan="3"><strong>TOTAL</strong></td>
                <td class="text-right" style="color:#dc2626"><strong>${fmt(summary.totalHPP)}</strong></td>
                <td class="text-right" style="color:#2563eb"><strong>${fmt(summary.totalJual)}</strong></td>
                <td class="text-right" style="color:${summary.totalMargin >= 0 ? '#16a34a' : '#dc2626'}"><strong>${fmt(summary.totalMargin)}</strong></td>
                <td class="text-center" style="color:${summary.marginPct >= 0 ? '#16a34a' : '#dc2626'}"><strong>${fmtPct(summary.marginPct)}</strong></td>
                <td></td>
              </tr>
            </tfoot>
          </table>

          <div class="footer">Margin Sample Laba Rugi © ${new Date().getFullYear()} Satala Dermatech</div>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(printHTML);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); }, 500);
  };

  const MetricCard = ({ label, value, sub, icon: Icon, color }) => (
    <Card className="border-[#E6D5B8]/40 shadow-sm bg-white rounded-2xl overflow-hidden">
      <CardContent className="p-5 flex items-start justify-between">
        <div className="flex-1">
          <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mb-1">{label}</p>
          <p className={`text-xl lg:text-2xl font-black ${color}`}>{value}</p>
          {sub && <p className="text-[10px] text-slate-400 font-semibold mt-1">{sub}</p>}
        </div>
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
          color.includes('green') ? 'bg-green-50 text-green-600' :
          color.includes('red') ? 'bg-red-50 text-red-600' :
          color.includes('blue') ? 'bg-blue-50 text-blue-600' : 'bg-[#F9F5EC] text-[#8C6208]'
        }`}>
          <Icon className="w-5 h-5" />
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6 text-[#302621]" ref={printRef}>
      
      {/* Header */}
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-black text-slate-800 tracking-tight">Margin Sample</h1>
          <p className="text-xs text-slate-500 mt-1">Laporan detail rugi & margin khusus invoice Sample.</p>
        </div>
        <Button 
          onClick={handleExportPDF} 
          className="bg-[#8C6208] text-white hover:bg-[#724F06] gap-2 text-xs font-bold rounded-xl h-10 shadow-sm shrink-0"
        >
          <FileDown className="w-4 h-4" /> Export PDF
        </Button>
      </div>

      {/* Filter Preset Bar */}
      <div className="bg-white border border-[#E6D5B8]/50 rounded-2xl p-5 space-y-4 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            <Calendar className="w-4 h-4 text-[#8C6208]" />
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Periode:</span>
            <div className="flex gap-1 flex-wrap">
              {PRESETS.map(p => (
                <button
                  key={p.label}
                  onClick={() => handlePreset(p)}
                  className={`px-3 py-1 rounded-full text-xs font-bold transition-all ${
                    !isCustom && activePreset === p.label
                      ? 'bg-[#302621] text-white'
                      : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                  }`}
                >
                  {p.label}
                </button>
              ))}
              <button
                onClick={() => setIsCustom(true)}
                className={`px-3 py-1 rounded-full text-xs font-bold transition-all ${
                  isCustom ? 'bg-[#302621] text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                }`}
              >
                Custom
              </button>
            </div>
          </div>
        </div>

        {isCustom && (
          <div className="flex items-center gap-2 flex-wrap border-t border-slate-100 pt-3">
            <CustomDatePicker value={dateStart} onChange={setDateStart} className="h-9 w-36 text-xs" />
            <span className="text-xs text-muted-foreground">hingga</span>
            <CustomDatePicker value={dateEnd} onChange={setDateEnd} className="h-9 w-36 text-xs" />
          </div>
        )}

        <p className="text-xs text-slate-400 font-semibold">
          Menampilkan <span className="font-bold text-[#8C6208]">{summary.count}</span> transaksi dari {dateStart} s/d {dateEnd}
        </p>
      </div>

      {/* KPI Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <MetricCard
          label="Total Transaksi"
          value={fmtNum(summary.count)}
          sub="invoice sample"
          icon={Package}
          color="text-[#8C6208]"
        />
        <MetricCard
          label="Total HPP / Modal"
          value={fmt(summary.totalHPP)}
          sub="total pengeluaran"
          icon={ArrowDownRight}
          color="text-rose-600"
        />
        <MetricCard
          label="Total Pembayaran"
          value={fmt(summary.totalJual)}
          sub="uang masuk aktual"
          icon={ArrowUpRight}
          color="text-blue-600"
        />
        <MetricCard
          label="Margin Keuntungan"
          value={fmt(summary.totalMargin)}
          sub={`${fmtPct(summary.marginPct)} dari penjualan`}
          icon={TrendingUp}
          color={summary.totalMargin >= 0 ? 'text-emerald-600' : 'text-rose-600'}
        />
      </div>

      {/* Charts Grid */}
      {filtered.length > 0 && (
        <div className="grid grid-cols-1 gap-6">
          <Card className="border-[#E6D5B8]/40 shadow-sm bg-white rounded-2xl">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-bold flex items-center gap-2 text-slate-700 uppercase tracking-wider">
                <BarChart2 className="w-4 h-4 text-[#8C6208]" />
                Grafik HPP, Penjualan & Margin per Brand (Top 10)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {chartDataBrand.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-12">Tidak ada data</p>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={chartDataBrand} margin={{ left: -10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                    <XAxis dataKey="brand" tick={{ fontSize: 9, fontWeight: 'bold' }} tickFormatter={v => v.length > 8 ? v.slice(0, 8) + '…' : v} />
                    <YAxis tick={{ fontSize: 9, fontWeight: 'bold' }} tickFormatter={v => `${(v / 1000000).toFixed(0)}jt`} />
                    <Tooltip formatter={(v) => fmt(v)} />
                    <Legend wrapperStyle={{ fontSize: 10, fontWeight: 'bold' }} />
                    <Bar dataKey="hpp" name="HPP" fill="#f87171" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="jual" name="Penjualan" fill="#60a5fa" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="margin" name="Margin" fill="#34d399" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Peringkat Margin Brand */}
      {allBrandData.length > 0 && (
        <Card className="border-[#E6D5B8]/40 shadow-sm bg-white rounded-2xl overflow-hidden mt-6">
          <CardHeader className="pb-4 bg-slate-50/40 border-b border-slate-100">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xs font-bold flex items-center gap-2 text-slate-700 uppercase tracking-wider">
                <TrendingUp className="w-4 h-4 text-[#8C6208]" />
                Peringkat Margin per Brand
              </CardTitle>
              <div className="text-right">
                <span className="text-[10px] text-slate-500 uppercase font-bold tracking-wider mr-2">Total Keseluruhan</span>
                <span className={`text-base font-black ${summary.totalMargin >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                  {summary.totalMargin >= 0 ? '+' : ''}{fmt(summary.totalMargin)}
                </span>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3">
              {allBrandData.map((b, idx) => (
                <div key={b.brand} className="flex items-center justify-between p-3 rounded-xl border border-slate-100 hover:bg-slate-50 transition-colors">
                  <div className="flex items-center gap-3 overflow-hidden">
                    <div className={`shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black ${idx < 3 ? 'bg-[#8C6208] text-white' : 'bg-slate-100 text-slate-500'}`}>
                      {idx + 1}
                    </div>
                    <span className="font-bold text-slate-700 text-xs truncate" title={b.brand}>
                      {b.brand.split(' / ')[0]}
                    </span>
                  </div>
                  <span className={`shrink-0 font-black text-xs ${b.margin >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {fmt(b.margin)}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Rincian Detail Table */}
      <Card className="border-[#E6D5B8]/40 shadow-sm bg-white rounded-2xl overflow-hidden mt-6">
        <CardHeader className="pb-2 bg-slate-50/40 border-b border-slate-100">
          <CardTitle className="text-xs font-bold flex items-center gap-2 text-slate-700 uppercase tracking-wider">
            <DollarSign className="w-4 h-4 text-[#8C6208]" />
            Rincian Margin per Invoice ({summary.count})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loadingPOs ? (
            <div className="p-6 text-center text-slate-400">Memuat rincian data...</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <TrendingUp className="w-12 h-12 mx-auto mb-3 opacity-20" />
              <p className="font-bold text-sm">Tidak ada data di periode ini</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs text-left">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 text-slate-500 font-bold uppercase tracking-wider">
                    <th className="p-4">Tanggal</th>
                    <th className="p-4">Nama Brand</th>
                    <th className="p-4">Produk</th>
                    <th className="p-4 text-right">HPP / Modal</th>
                    <th className="p-4 text-right">Total Pembayaran</th>
                    <th className="p-4 text-right">Total Margin</th>
                    <th className="p-4 text-center">% Margin</th>
                    <th className="p-4 text-center">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 font-semibold text-slate-700">
                  {filtered.map(r => {
                    const { hpp, jual, margin } = getRecordMetrics(r);
                    const status = String(r.status_bayar || 'Belum Bayar');
                    const belumBayar = status === 'Belum Bayar' || status === 'BELUM BAYAR' || status === 'BELUM DIBAYAR';
                    const pct = jual > 0 ? (margin / jual) * 100 : 0;
                    const isProfit = margin >= 0;
                    
                    const timestamp = parseInvoiceDate(r.tanggal);
                    const tanggal = timestamp ? format(new Date(timestamp), 'dd MMM yyyy', { locale: id }) : (r.tanggal || '—');

                    const isLunas = status === 'Sudah Lunas' || status.toUpperCase().includes('LUNAS');
                    const isPartial = (r.jumlah_dibayar > 0 && r.jumlah_dibayar < (r.total_harga_jual_semua || 0)) || status.toUpperCase().includes('DP') || status === 'Belum Lunas' || status.toUpperCase() === 'TEMPO';
                    const percentPaid = r.total_harga_jual_semua > 0 ? Math.round(((r.jumlah_dibayar || 0) / r.total_harga_jual_semua) * 100) : 0;

                    let displayStatus = status;
                    let badgeClass = 'bg-rose-100 text-rose-700';

                    if (isLunas) {
                      displayStatus = 'Sudah Lunas';
                      badgeClass = 'bg-green-100 text-green-700';
                    } else if (isPartial) {
                      displayStatus = `${percentPaid}% Menuju Lunas`;
                      if (percentPaid > 70) badgeClass = 'bg-green-100 text-green-700';
                      else if (percentPaid >= 40) badgeClass = 'bg-orange-100 text-orange-700';
                      else badgeClass = 'bg-red-100 text-red-700';
                    } else if (belumBayar) {
                      displayStatus = 'Belum Dibayar';
                      badgeClass = 'bg-rose-100 text-rose-700';
                    }

                    return (
                      <tr key={r.id} className="hover:bg-slate-50/50 transition-colors">
                        <td className="p-4 font-mono text-slate-400">{tanggal}</td>
                        <td className="p-4">
                          <span className="font-extrabold text-slate-800">
                            {r.nama_brand ? String(r.nama_brand).split(' / ')[0].trim() : '—'}
                          </span>
                          <span className="text-[10px] text-slate-400 font-semibold block mt-0.5">
                            <Link 
                              to={`/office/finance/invoices?search=${r.id}&expand=${r.id}`}
                              className="text-indigo-600 hover:text-indigo-800 hover:underline font-bold"
                            >
                              {r.id}
                            </Link>
                          </span>
                          <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                            <span className={`text-[9px] px-2 py-0.5 rounded-full font-black uppercase tracking-wider ${badgeClass}`}>
                              {displayStatus}
                            </span>
                            {r.jatuh_tempo && !isLunas && (
                              <span className="text-[9px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider bg-rose-50 text-rose-600 border border-rose-100 flex items-center gap-1">
                                Tempo: {r.jatuh_tempo}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="p-4 max-w-[120px] truncate" title={r.product_name}>{r.product_name}</td>
                        <td className="p-4 text-right text-rose-600">{fmt(hpp)}</td>
                        <td className="p-4 text-right text-blue-600">{fmt(jual)}</td>
                        <td className={`p-4 text-right font-extrabold ${isProfit ? 'text-emerald-600' : 'text-rose-600'}`}>
                          {isProfit ? '+' : ''}{fmt(margin)}
                        </td>
                        <td className="p-4 text-center">
                          <span className={`font-extrabold ${isProfit ? 'text-emerald-600' : 'text-rose-600'}`}>
                            {isProfit ? '' : '-'}{fmtPct(Math.abs(pct))}
                          </span>
                        </td>
                        <td className="p-4 text-center">
                          <Badge variant="outline" className={`text-[10px] ${isProfit ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-rose-50 text-rose-700 border-rose-200'}`}>
                            {isProfit ? 'Untung' : 'Rugi'}
                          </Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-50/80 border-t-2 border-slate-200 font-extrabold text-slate-800">
                    <td className="p-4" colSpan={3}>
                      <span className="text-xs uppercase tracking-widest text-slate-500 font-black">TOTAL</span>
                    </td>
                    <td className="p-4 text-right text-rose-600">{fmt(summary.totalHPP)}</td>
                    <td className="p-4 text-right text-blue-600">{fmt(summary.totalJual)}</td>
                    <td className={`p-4 text-right text-base font-black ${summary.totalMargin >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                      {summary.totalMargin >= 0 ? '+' : ''}{fmt(summary.totalMargin)}
                    </td>
                    <td className={`p-4 text-center font-black ${summary.marginPct >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                      {fmtPct(summary.marginPct)}
                    </td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

    </div>
  );
}
